// FahrzeugFreigabenEditor.jsx
// Zeigt eine Matrix aller Fahrzeuge x Funktionen. Ein Klick auf eine Kachel
// öffnet einen Grund-Dialog (bei Sperren) oder entsperrt (bei bereits gesperrt).
// Die Daten (gesperrte_funktionen) werden direkt am User-Datensatz gespeichert.

import React, { useState, useMemo } from 'react';
import { Ban, ShieldCheck, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { cn } from '../../lib/utils';

export function FahrzeugFreigabenEditor({
  fahrzeuge,          // [{id, funkrufname, kurzname, funktionen: [{kuerzel, name}]}]
  gesperrteFunktionen, // [{fahrzeug_id, funktion_kuerzel, grund}]
  onChange,           // (neueListe) => void
  readOnly = false,
}) {
  const [dialog, setDialog] = useState(null); // { fahrzeug, funktion, grund }

  const sperrenMap = useMemo(() => {
    const m = new Map();
    (gesperrteFunktionen || []).forEach(s => {
      m.set(`${s.fahrzeug_id}::${s.funktion_kuerzel}`, s);
    });
    return m;
  }, [gesperrteFunktionen]);

  const isGesperrt = (fahrzeugId, kuerzel) =>
    sperrenMap.get(`${fahrzeugId}::${kuerzel}`);

  const handleKachelClick = (fahrzeug, funktion) => {
    if (readOnly) return;
    const existing = isGesperrt(fahrzeug.id, funktion.kuerzel);
    if (existing) {
      // Entsperren
      const neu = (gesperrteFunktionen || []).filter(
        s => !(s.fahrzeug_id === fahrzeug.id && s.funktion_kuerzel === funktion.kuerzel)
      );
      onChange?.(neu);
    } else {
      // Sperr-Dialog öffnen
      setDialog({ fahrzeug, funktion, grund: '' });
    }
  };

  const handleSaveSperre = () => {
    if (!dialog) return;
    const neu = [
      ...(gesperrteFunktionen || []),
      {
        fahrzeug_id: dialog.fahrzeug.id,
        funktion_kuerzel: dialog.funktion.kuerzel,
        grund: (dialog.grund || '').trim() || null,
      },
    ];
    onChange?.(neu);
    setDialog(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-600" />
        <p>
          Standard: alle Funktionen erlaubt. Klick auf eine Funktion sperrt sie
          (Grund optional). Erneuter Klick auf eine gesperrte (rote) Funktion hebt die Sperre auf.
        </p>
      </div>

      <div className="space-y-2">
        {fahrzeuge.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            Keine Fahrzeuge konfiguriert.
          </p>
        )}
        {fahrzeuge.map((fz) => {
          const fns = (fz.funktionen || []).filter(f => f.kuerzel);
          if (fns.length === 0) return null;
          return (
            <div key={fz.id} className="rounded-md border p-2.5">
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-mono text-sm font-semibold">{fz.kurzname || fz.funkrufname}</span>
                <span className="text-xs text-muted-foreground truncate">{fz.funkrufname}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {fns.map((fn) => {
                  const gesperrt = isGesperrt(fz.id, fn.kuerzel);
                  return (
                    <button
                      key={fn.kuerzel}
                      type="button"
                      onClick={() => handleKachelClick(fz, fn)}
                      disabled={readOnly}
                      title={gesperrt?.grund ? `Gesperrt — ${gesperrt.grund}` : (gesperrt ? 'Gesperrt (kein Grund)' : 'Klick → sperren')}
                      className={cn(
                        'inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-xs font-mono transition-all',
                        gesperrt
                          ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                          : 'bg-emerald-50/40 border-emerald-200 text-emerald-800 hover:bg-emerald-100',
                        readOnly && 'opacity-70 cursor-not-allowed'
                      )}
                      data-testid={`freigabe-${fz.id}-${fn.kuerzel}`}
                    >
                      {gesperrt && <Ban className="w-3 h-3" />}
                      {fn.kuerzel}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-4 h-4 text-red-600" />
              Funktion sperren
            </DialogTitle>
            <DialogDescription>
              <strong>{dialog?.funktion?.kuerzel}</strong> auf{' '}
              <strong>{dialog?.fahrzeug?.funkrufname}</strong> für diesen Mitarbeiter sperren.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Grund (optional)</Label>
            <Input
              value={dialog?.grund || ''}
              onChange={(e) => setDialog((d) => ({ ...d, grund: e.target.value }))}
              placeholder="z. B. keine DLK-Einweisung, läuft ab 30.06."
              maxLength={300}
              autoFocus
              data-testid="sperre-grund-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Der Grund erscheint beim Einteilenden als Tooltip an der gesperrten Funktion.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)}>Abbrechen</Button>
            <Button onClick={handleSaveSperre} data-testid="sperre-save-btn">
              <Save className="w-4 h-4 mr-1" /> Sperren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FahrzeugFreigabenEditor;
